/**
 * Creative column: senders & texts picker.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { AiTextMetadataPayload } from '@/hooks';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import { SectionHeader } from '../../form/SectionHeader';

import { SendersPickerBlock } from './SendersPickerBlock';
import { TextsPickerBlock } from './TextsPickerBlock';

interface CreativeColumnProps {
  countryKey: string;
  routeId: number;
  senderNames: string[];
  setSenderNames: (updater: (prev: string[]) => string[]) => void;
  smsTexts: string[];
  smsTextsWithIds?: Array<{ text: string; templateId: string }>;
  setSmsTexts: (updater: (prev: string[]) => string[]) => void;
  selectedSenderNames: string[];
  setSelectedSenderNames: (updater: (prev: string[]) => string[]) => void;
  selectedSmsTexts: string[];
  setSelectedSmsTexts: (updater: (prev: string[]) => string[]) => void;
  senderSearch: string;
  setSenderSearch: (v: string) => void;
  textSearch: string;
  setTextSearch: (v: string) => void;
  newSenderValue: string;
  setNewSenderValue: (v: string) => void;
  newTextValue: string;
  setNewTextValue: (v: string) => void;
  newTextError: string | null;
  setNewTextError: (v: string | null) => void;
  onAddSender: (senderName: string) => Promise<void>;
  onAddSenders?: (senders: string[]) => Promise<void>;
  onAddTexts: (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => Promise<void>;
  onAddTextsDeselected?: (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => Promise<void>;
  onSaveGeneratedAndSelectOnly?: (texts: string[]) => void;
  onPerDropSmsTextsApplied?: (batches: string[][]) => void;
  onAiGeneratedDeselectOld?: () => void;
  onSaveGeneratedSendersAndSelectOnly?: (senders: string[]) => void;
  onPerDropSendersApplied?: (batches: string[][]) => void;
  onAiGeneratedSendersDeselectOld?: () => void;
  onMarkSendersForHide?: (senders: string[]) => void;
  pendingHideSenders?: string[];
  onDeleteSender: (senderName: string) => Promise<void>;
  onDeleteSenders?: (senderNames: string[]) => Promise<void>;
  onDeleteText: (smsText: string) => Promise<void>;
  onDeleteTextsBulk?: (smsTexts: string[]) => Promise<void>;
  onMarkTextsForHide?: (texts: string[]) => void;
  pendingHideTexts?: string[];
  maxTexts?: number;
  tagsByTextId?: Record<string, string[]>;
  tagColors?: Record<string, string>;
  isBusy: boolean;
  dropCount?: number;
  dropLabels?: DropLabel[];
}

export const CreativeColumn: FC<CreativeColumnProps> = (props) => {
  const {
    countryKey,
    routeId,
    senderNames,
    setSenderNames,
    smsTexts,
    smsTextsWithIds,
    setSmsTexts,
    selectedSenderNames,
    setSelectedSenderNames,
    selectedSmsTexts,
    setSelectedSmsTexts,
    senderSearch,
    setSenderSearch,
    textSearch,
    setTextSearch,
    newSenderValue,
    setNewSenderValue,
    newTextValue,
    setNewTextValue,
    newTextError,
    setNewTextError,
    onAddSender,
    onAddSenders,
    onAddTexts,
    onAddTextsDeselected,
    onSaveGeneratedAndSelectOnly,
    onPerDropSmsTextsApplied,
    onAiGeneratedDeselectOld,
    onSaveGeneratedSendersAndSelectOnly,
    onPerDropSendersApplied,
    onAiGeneratedSendersDeselectOld,
    onMarkSendersForHide,
    pendingHideSenders,
    onDeleteSender,
    onDeleteSenders,
    onDeleteText,
    onDeleteTextsBulk,
    onMarkTextsForHide,
    pendingHideTexts,
    maxTexts,
    tagsByTextId,
    tagColors,
    isBusy,
    dropCount = 1,
    dropLabels,
  } = props;

  const [showAddSender, setShowAddSender] = useState(senderNames.length === 0);
  const [showAddText, setShowAddText] = useState(smsTexts.length === 0);
  const [aiSendersActive, setAiSendersActive] = useState(false);
  const [aiTextsActive, setAiTextsActive] = useState(false);
  const onAiSendersActive = useCallback((active: boolean) => setAiSendersActive(active), []);
  const onAiTextsActive = useCallback((active: boolean) => setAiTextsActive(active), []);

  useEffect(() => {
    if (senderNames.length === 0) setShowAddSender(true);
  }, [senderNames.length]);
  useEffect(() => {
    if (smsTexts.length === 0) setShowAddText(true);
  }, [smsTexts.length]);

  return (
    <div className="creative-column-container flex min-h-[320px] min-w-0 shrink-0 flex-col overflow-hidden border-b border-border xl:min-h-0 xl:min-w-[320px] xl:flex-1 xl:shrink xl:border-b-0 xl:border-r">
      <SectionHeader icon={MessageSquare} title="Creative" subtitle="Senders & Texts" />

      <div
        className={cn(
          'creative-grid max-h-[58vh] min-h-0 flex-1 overflow-hidden p-3 xl:max-h-none',
          aiSendersActive && 'creative-grid-ai-senders',
          aiTextsActive && !aiSendersActive && 'creative-grid-ai-texts',
        )}
      >
        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-col overflow-hidden',
            aiSendersActive && !aiTextsActive && 'flex-1',
            aiTextsActive && 'hidden',
          )}
        >
          <SendersPickerBlock
          countryKey={countryKey}
          routeId={routeId}
          senderNames={senderNames}
          selectedSenderNames={selectedSenderNames}
          senderSearch={senderSearch}
          setSenderSearch={setSenderSearch}
          showAddSender={showAddSender}
          setShowAddSender={setShowAddSender}
          newSenderValue={newSenderValue}
          setNewSenderValue={setNewSenderValue}
          onAddSender={onAddSender}
          onAddSenders={onAddSenders}
          onDeleteSender={onDeleteSender}
          onDeleteSenders={onDeleteSenders}
          setSenderNames={setSenderNames}
          setSelectedSenderNames={setSelectedSenderNames}
          isBusy={isBusy}
          onSaveGeneratedSendersAndSelectOnly={onSaveGeneratedSendersAndSelectOnly}
          onPerDropSendersApplied={onPerDropSendersApplied}
          onAiGeneratedSendersDeselectOld={onAiGeneratedSendersDeselectOld}
          onMarkSendersForHide={onMarkSendersForHide}
          pendingHideSenders={pendingHideSenders}
          dropCount={dropCount}
          dropLabels={dropLabels}
          onAiSendersActive={onAiSendersActive}
        />
        </div>

        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
            aiSendersActive && !aiTextsActive && 'hidden',
          )}
        >
        <TextsPickerBlock
          countryKey={countryKey}
          smsTexts={smsTexts}
          smsTextsWithIds={smsTextsWithIds}
          selectedSmsTexts={selectedSmsTexts}
          textSearch={textSearch}
          setTextSearch={setTextSearch}
          showAddText={showAddText}
          setShowAddText={setShowAddText}
          newTextValue={newTextValue}
          setNewTextValue={setNewTextValue}
          newTextError={newTextError}
          setNewTextError={setNewTextError}
          onAddTexts={onAddTexts}
          onAddTextsDeselected={onAddTextsDeselected}
          onSaveGeneratedAndSelectOnly={onSaveGeneratedAndSelectOnly}
          onPerDropSmsTextsApplied={onPerDropSmsTextsApplied}
          onAiGeneratedDeselectOld={onAiGeneratedDeselectOld}
          onDeleteText={onDeleteText}
          onDeleteTextsBulk={onDeleteTextsBulk}
          onMarkTextsForHide={onMarkTextsForHide}
          pendingHideTexts={pendingHideTexts}
          maxTexts={maxTexts}
          setSmsTexts={setSmsTexts}
          setSelectedSmsTexts={setSelectedSmsTexts}
          tagsByTextId={tagsByTextId}
          tagColors={tagColors}
          isBusy={isBusy}
          dropCount={dropCount}
          dropLabels={dropLabels}
          onAiTextsActive={onAiTextsActive}
        />
        </div>
      </div>
    </div>
  );
};

export default CreativeColumn;
